import uploadApi from '@/services/plataforma/upload';
import uploadApiV2 from '@/services/plataforma/contentv2/upload';

export default {
  async actionUploadFile({ dispatch }, { file, type, courseId, isInteractiveZip = false, folderName = null }) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!window.navigator.onLine) {
      throw new Error(`[UPL-001] ${this.app.i18n.t('error.networkErrorOffline')}`);
    }

    if (!fileExtension || fileExtension === file.name) {
      throw new Error(`[UPL-002] ${this.app.i18n.t('taskEditor.invalidFileName')}`);
    }

    if (!file || !(file instanceof File)) {
      throw new Error(`[UPL-003] ${this.app.i18n.t('taskEditor.invalidFile')}`);
    }

    if (file.size === 0) {
      throw new Error(`[UPL-004] ${this.app.i18n.t('taskEditor.emptyFile')}`);
    }

    if (!file.type) {
      throw new Error(`[UPL-005] ${this.app.i18n.t('taskEditor.invalidFileType')}`);
    }

    // FIX: net::ERR_FILE_NOT_FOUND
    // Real-world scenario: User selects a file from their computer, then moves/renames/deletes it
    // BEFORE clicking "Send". The File object reference becomes invalid, causing a silent failure
    // or cryptic browser error. This validation catches the issue early with a clear error message.
    // We read only 1KB (fast, works for any file size) to verify the file is still accessible.
    try {
      await file.slice(0, 1024).arrayBuffer();
    } catch (error) {
      throw new Error(`[UPL-006] ${this.app.i18n.t('taskEditor.fileNotAccessible')}`);
    }
    // FIX: net::ERR_UPLOAD_FILE_CHANGED on Android/Mobile.
    // When selecting files from cloud providers (Google Drive, OneDrive) on Android, the temporary file reference
    // can become invalid or 'changed' by the OS before the upload completes.
    // Cloning the file into memory (ArrayBuffer) creates a stable reference and prevents this error.
    // We limit this to 100MB to avoid Out Of Memory (OOM) crashes on low-end devices.
    let fileToUpload = file;
    try {
      const oneHundredMB = 100 * 1024 * 1024;
      if (file.size < oneHundredMB) {
        const buffer = await file.arrayBuffer();
        fileToUpload = new File([buffer], file.name, { type: file.type });
      }
    } catch {
      // Intentionally ignore - fallback to original file if cloning fails
    }

    const fileExtForUpload = `.${isInteractiveZip ? 'interactive-zip' : fileExtension}`;
    const useMediaBucket = type === 'recording';

    return uploadApi
      .getSignature({
        folderName    : folderName || courseId,
        fileExtension : fileExtForUpload,
        useMediaBucket
      })
      .then(({ data: { key, url } }) => {
        return dispatch('actionUploadInS3', { url, file: fileToUpload })
          .then(() => {
            const cdnBaseUrl = useMediaBucket ? process.env.S3_CDN_MEDIA_URL : process.env.S3_CDN_URL;
            const cdnUrl = new URL(key, cdnBaseUrl);

            return Promise.resolve({
              url      : cdnUrl.href,
              fileKey  : key,
              fileName : file.name,
              fileSize : file.size
            });
          });
      })
      .catch(e => {
        if (e.response?.data?.error?.message) {
          throw new Error(`[UPL-008] ${this.app.i18n.t('taskEditor.uploadErrorMessage')}${e.response.data.error.message}`);
        }
        throw new Error(`[UPL-009] ${this.app.i18n.t('taskEditor.uploadError')}`);
      });
  },
  async actionUploadInS3(_, { url, file }) {
    const MAX_RETRIES = 3;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await uploadApi.uploadFile(url, file);
        return Promise.resolve(result);
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          const backoffMilliseconds = 3000;
          const backoffMultiplierResult = backoffMilliseconds * attempt;
          await new Promise(resolve => setTimeout(resolve, backoffMultiplierResult));
          continue;
        }

        return Promise.reject(error);
      }
    }
  },
  actionGetAnnotationSignatureV2(_, { academicMainId, correctionId, enrollmentTaskAttachmentId, isLegacy }) {
    return uploadApiV2
      .getCorrectionSignature({ academicMainId, correctionId, enrollmentTaskAttachmentId, isLegacy, fileType: 'txt' })
      .then(result => {
        return Promise.resolve(result.data);
      })
      .catch(e => {
        return Promise.reject(e.response);
      });
  },
  actionGetCorrectionSignatureV2(_, { academicMainId, correctionId, enrollmentTaskAttachmentId, isLegacy }) {
    return uploadApiV2
      .getCorrectionSignature({ academicMainId, correctionId, enrollmentTaskAttachmentId, isLegacy, fileType: 'pdf' })
      .then(result => {
        return Promise.resolve(result.data);
      })
      .catch(e => {
        return Promise.reject(e.response);
      });
  },
  actionUploadCorrectionFileInS3(_, { signature, file }) {
    return uploadApi
      .uploadFile(signature.writeSignature.url, file)
      .then(() => {
        return Promise.resolve({
          url: `${process.env.S3_CDN_URL}/${signature.writeSignature.key}`
        });
      })
      .catch(e => {
        return Promise.reject(e);
      });
  }
};
